// src/services/SyncService.js - Background Cart Synchronization
import logger from '../utils/logger';
import cartManager from './CartManager';

/**
 * SyncService - Handles background synchronization between localStorage and server
 *
 * Features:
 * - 5-second sync intervals
 * - Passive conflict resolution (never block user)
 * - Intelligent batching
 * - Retry logic with exponential backoff
 * - Online/offline detection
 */

class SyncService {
  constructor() {
    // Configuration
    this.config = {
      syncInterval: 30000,       // 30 seconds (reduced frequency for less server load)
      maxRetries: 3,             // Max retry attempts
      retryDelay: 1000,          // Initial retry delay (ms)
      maxRetryDelay: 10000,      // Maximum retry delay (ms)
      batchSize: 10,             // Max operations per sync
      conflictStrategy: 'passive', // Never block user operations
      immediateSync: true,       // Enable immediate sync on changes
      batchingDelay: 500,        // Delay for batching multiple rapid changes (500ms for responsive discounts)
      maxQueueSize: 1000,        // Maximum queue size to prevent memory overflow
      maxOperationAge: 3600000   // Max age of operations in ms (1 hour)
    };

    // State
    this.isRunning = false;
    this.isSyncing = false;
    this.isOnline = navigator.onLine;
    this.syncTimer = null;
    this.batchTimer = null;
    this.retryCount = 0;
    this.pendingChanges = false;

    // Live cart updates (WebSocket)
    this.cartSocket = null;
    this.wsReconnectTimer = null;
    this.wsReconnectDelay = 1000;
    this.wsRefreshTimer = null;
    this.wsRefreshPending = false;
    this.wsCatchupNeeded = false;
    this.lastServerTimestamp = null;
    
    // API endpoints (without /api/ prefix since api client adds it)
    this.endpoints = {
      batch: '/carts/batch/',
      get: '/carts/',
      clear: '/carts/'
    };

    // Setup event listeners
    this._setupEventListeners();
    
    logger.log('[SyncService] Initialized with event-driven sync (30-second fallback interval)');
  }

  // === Public API ===

  /**
   * Start the sync service
   */
  start() {
    if (this.isRunning) {
      logger.log('[SyncService] Already running');
      return;
    }

    this.isRunning = true;
    this._scheduleNextSync();
    this._connectCartSocket();
    logger.log('[SyncService] Started background sync');
  }

  /**
   * Stop the sync service
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    
    // Clear all timers to prevent memory leaks
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    
    this._disconnectCartSocket();

    // Reset state
    this.isSyncing = false;
    this.retryCount = 0;
    this.pendingChanges = false;

    logger.log('[SyncService] Stopped background sync and cleared all timers');
  }

  // === Live cart updates (WebSocket) ===

  /**
   * Connect to the per-user cart WebSocket so changes made on other
   * devices are reflected here within seconds.
   * @private
   */
  _connectCartSocket() {
    if (typeof WebSocket === 'undefined') return;
    if (this.cartSocket &&
        (this.cartSocket.readyState === WebSocket.OPEN ||
         this.cartSocket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      logger.log('[SyncService] No access token - cart socket not connected');
      return;
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      let host = window.location.host;
      if (host.includes('localhost:3000') || host.includes('127.0.0.1:3000')) {
        host = host.replace(':3000', ':8000');
      }
      const socket = new WebSocket(`${protocol}//${host}/ws/cart/?token=${token}`);

      socket.onopen = () => {
        logger.log('[SyncService] Cart socket connected');
        this.wsReconnectDelay = 1000;
        if (this.wsCatchupNeeded) {
          this.wsCatchupNeeded = false;
          this._applyServerRefresh();
        }
      };
      socket.onmessage = (event) => this._handleCartSocketMessage(event);
      socket.onclose = () => {
        this.cartSocket = null;
        this.wsCatchupNeeded = true;
        this._scheduleCartSocketReconnect();
      };
      socket.onerror = () => {
        try { socket.close(); } catch (e) { /* already closed */ }
      };

      this.cartSocket = socket;
    } catch (error) {
      logger.warn('[SyncService] Cart socket connection failed:', error);
      this._scheduleCartSocketReconnect();
    }
  }

  /**
   * @private
   */
  _disconnectCartSocket() {
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = null;
    }
    if (this.wsRefreshTimer) {
      clearTimeout(this.wsRefreshTimer);
      this.wsRefreshTimer = null;
    }
    if (this.cartSocket) {
      const socket = this.cartSocket;
      this.cartSocket = null;
      socket.onclose = null;
      try { socket.close(); } catch (e) { /* already closed */ }
    }
    this.wsRefreshPending = false;
    this.wsCatchupNeeded = false;
  }

  /**
   * @private
   */
  _scheduleCartSocketReconnect() {
    if (!this.isRunning || this.wsReconnectTimer) return;
    this.wsReconnectTimer = setTimeout(() => {
      this.wsReconnectTimer = null;
      if (this.isRunning) {
        this._connectCartSocket();
      }
    }, this.wsReconnectDelay || 1000);
    this.wsReconnectDelay = Math.min((this.wsReconnectDelay || 1000) * 2, 30000);
  }

  /**
   * Handle a cart_updated push from the server.
   * @private
   */
  _handleCartSocketMessage(event) {
    try {
      const data = JSON.parse(event.data);
      if (data.type !== 'cart_updated') return;

      // Skip echoes of this device's own syncs: notifications at or before
      // the server timestamp we last saw are our own changes coming back.
      if (data.timestamp && this.lastServerTimestamp &&
          new Date(data.timestamp).getTime() <= new Date(this.lastServerTimestamp).getTime()) {
        return;
      }

      this._applyServerRefresh();
    } catch (error) {
      logger.warn('[SyncService] Error handling cart socket message:', error);
    }
  }

  /**
   * Refresh the local cart from the server, but only when idle — never
   * while the user has unsent local changes (the refresh would wipe them).
   * When busy, the refresh is deferred until the queue drains.
   * @private
   */
  _applyServerRefresh() {
    const idle = !this.isSyncing && !this.batchTimer &&
      cartManager.getSyncQueue().length === 0;
    if (!idle) {
      this.wsRefreshPending = true;
      return;
    }
    if (this.wsRefreshTimer) return;
    this.wsRefreshTimer = setTimeout(() => {
      this.wsRefreshTimer = null;
      this.refreshFromServer();
    }, 300);
  }

  /**
   * Enhanced cleanup method for memory management
   * Should be called when service is no longer needed
   */
  cleanup() {
    this.stop();
    
    // Remove event listeners to prevent memory leaks
    window.removeEventListener('online', this._onlineHandler);
    window.removeEventListener('offline', this._offlineHandler);
    document.removeEventListener('visibilitychange', this._visibilityHandler);
    window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    
    // Clear any accumulated sync queue to prevent memory bloat
    this._clearSyncQueue();
    
    // Reset state to prevent memory retention
    this.pendingChanges = false;
    this.retryCount = 0;
    this.isSyncing = false;
    this.isRunning = false;

    logger.log('[SyncService] Enhanced cleanup completed - all timers, listeners, and state cleared');
  }

  /**
   * Clear sync queue to prevent memory accumulation
   * @private
   */
  _clearSyncQueue() {
    try {
      const queueSize = cartManager.getSyncQueueSize();
      if (queueSize > 50) { // If queue gets too large
        logger.warn(`[SyncService] Large sync queue detected (${queueSize} items), clearing`);
        cartManager.clearSyncQueue();
      }
    } catch (error) {
      logger.error('[SyncService] Error clearing sync queue:', error);
    }
  }

  /**
   * Force immediate sync
   * @returns {Promise<boolean>} Success status
   */
  async forceSync() {
    if (!this.isOnline) {
      logger.log('[SyncService] Cannot force sync - offline');
      return false;
    }

    // Cancel any scheduled sync to avoid conflicts
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    // Cancel batching timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    const result = await this._performSync();

    // Only reschedule if service is still running
    if (this.isRunning && !this.syncTimer) {
      this._scheduleNextSync();
    }

    return result;
  }

  /**
   * Refresh cart from server - fetches latest cart data including discounts
   * NOTE: This replaces entire cart - use refreshDiscounts() for discount-only updates
   * @returns {Promise<boolean>} Success status
   */
  async refreshFromServer() {
    if (!this.isOnline) {
      logger.log('[SyncService] Cannot refresh from server - offline');
      return false;
    }

    try {
      logger.log('[SyncService] Refreshing cart from server...');
      const serverCart = await this._fetchServerCart();

      if (serverCart) {
        logger.log(`[SyncService] Received server cart with ${serverCart.items?.length || 0} items`);
        this.lastServerTimestamp = serverCart.updated_at_iso || serverCart.updated_at || this.lastServerTimestamp;
        cartManager.replaceWithServerCart(serverCart);
        // Server-computed discounts lack the locally selected customer's
        // context (sales reps) and base_discounted_price — recalculate
        // locally, same as the checkout reconcile does.
        await cartManager.calculateDiscounts();
        return true;
      }

      logger.warn('[SyncService] No server cart data received');
      return false;
    } catch (error) {
      logger.error('[SyncService] Error refreshing from server:', error);
      return false;
    }
  }

  /**
   * Reconcile the local cart with the server cart before checkout.
   * Flushes the pending queue, fetches the server cart (the union of all
   * devices' synced changes) and adopts it locally, returning a diff so the
   * checkout page can show the customer what changed. Every failure path
   * fails open: the local cart is left untouched and checkout proceeds as
   * before.
   * @returns {Promise<{status: string, changes: Object|null}>}
   */
  async reconcileForCheckout() {
    if (!this.isOnline) {
      return { status: 'offline', changes: null };
    }

    try {
      const localItems = cartManager.getCart().items || [];

      await this.forceSync();

      if (cartManager.getSyncQueue().length > 0) {
        // Unflushed local operations would be wiped by adopting the server
        // cart, so keep the local cart authoritative for this checkout.
        logger.warn('[SyncService] Reconcile skipped: sync queue still has pending operations');
        return { status: 'unsynced', changes: null };
      }

      const serverCart = await this._fetchServerCart();
      if (!serverCart || !Array.isArray(serverCart.items)) {
        return { status: 'error', changes: null };
      }

      if (serverCart.items.length === 0 && localItems.length > 0) {
        // The cart this device holds may have already become an order from
        // another device. A server-derived local id lets us ask directly;
        // if it was ordered, clear the stale items locally (never a synced
        // clear — the new server cart may already be in use) and tell the
        // customer which order it became.
        const localId = cartManager.getCart().id;
        if (typeof localId === 'string' && localId.startsWith('server-cart-')) {
          const lookup = await this._lookupOrderForCart(localId);
          if (lookup && lookup.ordered) {
            cartManager.replaceWithServerCart(serverCart);
            return { status: 'already_ordered', changes: null, order: lookup };
          }
        }
        // Unknown cause — keep the local cart and let the checkout page
        // warn the customer.
        return { status: 'server_empty', changes: null };
      }

      const keyOf = (item) => `${item.product?.id}::${item.unit?.id}`;
      const localMap = new Map(localItems.map(item => [keyOf(item), item]));
      const serverMap = new Map(serverCart.items.map(item => [keyOf(item), item]));

      const added = [];
      const removed = [];
      const quantityChanged = [];

      for (const [key, serverItem] of serverMap) {
        const localItem = localMap.get(key);
        if (!localItem) {
          added.push({
            key,
            name: serverItem.product?.name,
            quantity: parseFloat(serverItem.quantity),
            unit: serverItem.unit?.symbol || ''
          });
        } else if (Math.abs(parseFloat(serverItem.quantity) - parseFloat(localItem.quantity)) > 0.0001) {
          quantityChanged.push({
            key,
            name: serverItem.product?.name,
            from: parseFloat(localItem.quantity),
            to: parseFloat(serverItem.quantity),
            unit: serverItem.unit?.symbol || ''
          });
        }
      }

      for (const [key, localItem] of localMap) {
        if (!serverMap.has(key)) {
          removed.push({
            key,
            name: localItem.product?.name,
            quantity: parseFloat(localItem.quantity),
            unit: localItem.unit?.symbol || ''
          });
        }
      }

      cartManager.replaceWithServerCart(serverCart);
      // base_discounted_price and friends are computed locally, never
      // serialized by the server — recalculate so the order payload keeps
      // the same discount fields as a normal local-first cart.
      await cartManager.calculateDiscounts();

      const hasChanges = added.length > 0 || removed.length > 0 || quantityChanged.length > 0;
      logger.log(`[SyncService] Checkout reconcile complete: +${added.length} added, -${removed.length} removed, ~${quantityChanged.length} quantity changes`);

      return { status: 'ok', changes: hasChanges ? { added, removed, quantityChanged } : null };
    } catch (error) {
      logger.error('[SyncService] Checkout reconcile failed:', error);
      return { status: 'error', changes: null };
    }
  }

  /**
   * Ask the server whether the cart behind a checkout dedup key was
   * already turned into an order (scoped server-side to the user's own
   * orders). Returns the lookup payload or null on any failure.
   * @param {string} cartUuid - Local cart id (server-cart-<row id>)
   * @returns {Promise<Object|null>}
   * @private
   */
  async _lookupOrderForCart(cartUuid) {
    try {
      const { default: apiClient } = await import('./api');
      const response = await apiClient.get('/orders/by-cart-uuid/', {
        params: { cart_uuid: cartUuid }
      });
      return response.data;
    } catch (error) {
      logger.warn('[SyncService] Order lookup for cart failed:', error);
      return null;
    }
  }

  /**
   * Refresh discount data from server without replacing cart items
   * Keeps local cart items authoritative, only updates discount fields
   * @returns {Promise<boolean>} Success status
   */
  async refreshDiscounts() {
    if (!this.isOnline) {
      logger.log('[SyncService] Cannot refresh discounts - offline');
      return false;
    }

    try {
      // First, ensure any pending operations are synced to server
      // so the server has all items to calculate discounts for
      const queue = cartManager.getSyncQueue();
      if (queue.length > 0) {
        logger.log('[SyncService] Syncing pending operations before refreshing discounts...');
        await this.forceSync();
      }

      logger.log('[SyncService] Refreshing discounts from server...');
      const serverCart = await this._fetchServerCart();

      if (serverCart && serverCart.items) {
        logger.log(`[SyncService] Updating discounts for ${serverCart.items.length} items`);
        this._updateDiscountData(serverCart.items);
        return true;
      }

      logger.warn('[SyncService] No server cart data received for discount refresh');
      return false;
    } catch (error) {
      logger.error('[SyncService] Error refreshing discounts:', error);
      return false;
    }
  }

  /**
   * Trigger sync due to cart changes (event-driven)
   * Uses batching to avoid excessive syncing during rapid changes
   */
  triggerSync() {
    if (!this.isRunning || !this.isOnline) {
      this.pendingChanges = true;
      return;
    }

    logger.log('[SyncService] Cart change detected - scheduling sync');

    if (!this.config.immediateSync) {
      this.pendingChanges = true;
      return;
    }

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    this.batchTimer = setTimeout(() => {
      if (!this.isRunning) {
        clearTimeout(this.batchTimer);
        this.batchTimer = null;
        return;
      }

      logger.log('[SyncService] Batching period ended - triggering sync');
      this.batchTimer = null;
      this.pendingChanges = false;

      this._performSync();
    }, this.config.batchingDelay);
  }

  /**
   * Handle user login - fetch and merge server cart
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} Success status
   */
  async handleLogin(userId) {
    try {
      logger.log(`[SyncService] Handling login for user: ${userId}`);

      const prevConfig = JSON.parse(localStorage.getItem('cart_config') || '{}');
      if (prevConfig.userId && String(prevConfig.userId) !== String(userId)) {
        logger.log(`[SyncService] Previous cart belongs to user ${prevConfig.userId}, clearing for user ${userId}`);
        cartManager.cleanup();
      }

      // Initialize cart manager for user
      cartManager.initializeForUser(userId);

      // Drain any pending sync operations (e.g. deletions) BEFORE fetching server cart
      // This prevents deleted items from reappearing when server cart overwrites local
      const pendingQueue = cartManager.getSyncQueue();
      if (pendingQueue.length > 0) {
        logger.log(`[SyncService] Draining ${pendingQueue.length} pending sync operations before fetching server cart`);
        this.isSyncing = false; // Ensure _performSync doesn't skip
        this.isOnline = true;
        await this._performSync();
      }

      // ALWAYS fetch server cart on login to ensure we have the latest data
      // This is important for cross-browser/cross-device sync
      logger.log('[SyncService] Fetching server cart...');
      const serverCart = await this._fetchServerCart();
      const localCart = cartManager.getCart();

      logger.log(`[SyncService] Local cart: ${localCart.items?.length || 0} items, Server cart: ${serverCart?.items?.length || 0} items`);

      if (serverCart && serverCart.items && serverCart.items.length > 0) {
        // Compare timestamps to decide which cart to use
        const serverUpdatedAt = serverCart.updated_at_iso || serverCart.updated_at;
        const serverTimestamp = serverUpdatedAt ? new Date(serverUpdatedAt).getTime() : 0;
        const localTimestamp = localCart.updated_at_iso ? new Date(localCart.updated_at_iso).getTime() : 0;

        logger.log(`[SyncService] Server timestamp: ${serverTimestamp}, Local timestamp: ${localTimestamp}`);

        if (localCart.items && localCart.items.length > 0) {
          // Both have items - use the newer one
          if (serverTimestamp > localTimestamp) {
            logger.log('[SyncService] Server cart is newer, using server cart');
            cartManager.replaceWithServerCart(serverCart);
          } else {
            logger.log('[SyncService] Local cart is newer or same age, keeping local cart');
            // Update discount data from server since server has customer context
            logger.log('[SyncService] Updating discount data from server for local cart');
            this._updateDiscountData(serverCart.items);
          }
        } else {
          // Local cart empty, use server cart
          logger.log('[SyncService] Local cart empty, using server cart');
          cartManager.replaceWithServerCart(serverCart);
        }
      } else if (localCart.items && localCart.items.length > 0) {
        logger.log('[SyncService] Server cart empty but local has items, keeping local and syncing to server');
        // Mark items as pending so they get synced to server
        this.pendingChanges = true;
      } else {
        logger.log('[SyncService] Both carts empty');
      }

      // Safety net: if sync queue still has pending removals (e.g. sync failed),
      // apply them locally so deleted items don't reappear
      const remainingQueue = cartManager.getSyncQueue();
      const pendingRemovals = remainingQueue.filter(op => op.type === 'remove');
      if (pendingRemovals.length > 0) {
        logger.log(`[SyncService] Applying ${pendingRemovals.length} pending removals to loaded cart`);
        const cart = cartManager.getCart();
        let removedCount = 0;
        for (const removal of pendingRemovals) {
          const idx = cart.items.findIndex(item => item.server_id === removal.item_id);
          if (idx !== -1) {
            cart.items.splice(idx, 1);
            removedCount++;
          }
        }
        if (removedCount > 0) {
          cartManager._updateCartMetadata(cart);
          cartManager._saveCart(cart);
          cartManager._notifyListeners();
          logger.log(`[SyncService] Removed ${removedCount} items that were pending deletion`);
        }
      }

      // Mark that we've logged in this session (for page reload detection)
      localStorage.setItem('has_logged_in_session', 'true');

      // Start sync service to save changes to server
      logger.log('[SyncService] Starting sync service for background saves');
      this.start();

      // Server cart already has discounts calculated with full cart context
      // No need to recalculate - replaceWithServerCart already applied them

      return true;
    } catch (error) {
      logger.error('[SyncService] Error handling login:', error);

      // Still start sync service even if initial fetch fails
      logger.log('[SyncService] Starting sync service despite error');
      this.start();
      return false;
    }
  }

  /**
   * Handle user logout - final sync and cleanup
   * @returns {Promise<boolean>} Success status
   */
  async handleLogout() {
    try {
      logger.log('[SyncService] Handling logout');

      // Flush pending operations only. NEVER clear-and-re-add: rebuilding
      // the server cart deletes and recreates every row, so other devices'
      // item references (server_ids) go stale and their later quantity
      // updates fail silently — that's how one phone's logout used to make
      // another device's changes vanish. The incremental sync already keeps
      // the server correct; a queue flush is all a logout needs.
      if (this.isOnline) {
        try {
          await this.forceSync();
          logger.log('[SyncService] Logout: pending operations flushed');
        } catch (syncError) {
          logger.error('[SyncService] Error flushing sync queue on logout:', syncError);
        }
      }

      // Stop sync service
      this.stop();

      // Clear discount cache
      cartManager.clearDiscountCache();

      // Cleanup cart manager
      cartManager.cleanup();

      // Clear the login session flag
      localStorage.removeItem('has_logged_in_session');

      return true;
    } catch (error) {
      logger.error('[SyncService] Error handling logout:', error);
      return false;
    }
  }

  // === Private Methods ===

  /**
   * Setup event listeners for online/offline and page visibility
   * @private
   */
  _setupEventListeners() {
    // Store bound handler functions for cleanup
    this._onlineHandler = () => {
      logger.log('[SyncService] Back online');
      this.isOnline = true;
      this.retryCount = 0; // Reset retry count when coming back online

      if (this.isRunning) {
        // Sync immediately when back online if we have pending changes
        if (this.pendingChanges || cartManager.getSyncQueue().length > 0) {
          logger.log('[SyncService] Syncing pending changes after coming back online');
          this._performSync();
        } else {
          // Resume normal scheduling
          this._scheduleNextSync();
        }
      }
    };

    this._offlineHandler = () => {
      logger.log('[SyncService] Gone offline');
      this.isOnline = false;
    };

    this._visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.isOnline && this.isRunning) {
        // Sync when page becomes visible
        this._performSync();
      }
    };

    this._beforeUnloadHandler = () => {
      if (this.isOnline && this.isRunning) {
        // Try to sync before page unload (may not complete)
        this._performSync();
      }
    };

    // Add event listeners
    window.addEventListener('online', this._onlineHandler);
    window.addEventListener('offline', this._offlineHandler);
    document.addEventListener('visibilitychange', this._visibilityHandler);
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
  }

  /**
   * Schedule next sync with exponential backoff on retries
   * @private
   */
  _scheduleNextSync() {
    if (!this.isRunning) {
      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }
      return;
    }

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    let delay = this.config.syncInterval;
    if (this.retryCount > 0) {
      delay = Math.min(
        this.config.retryDelay * Math.pow(2, this.retryCount - 1),
        this.config.maxRetryDelay
      );
      logger.log(`[SyncService] Scheduling retry ${this.retryCount} in ${delay}ms`);
    }

    this.syncTimer = setTimeout(() => {
      if (this.isRunning) {
        this._performSync();
      }
    }, delay);
  }

  /**
   * Perform synchronization
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async _performSync() {
    // Skip if already syncing or offline
    if (this.isSyncing || !this.isOnline) {
      // Don't schedule here - finally block will handle it
      return false;
    }

    try {
      this.isSyncing = true;

      // Get operations queue and clean it up
      let queue = cartManager.getSyncQueue();
      queue = this._cleanupQueue(queue);

      if (queue.length === 0 && !this.pendingChanges) {
        // No operations to sync and no pending changes
        // Don't schedule here - finally block will handle it
        return true;
      }

      logger.log(`[SyncService] Syncing ${queue.length} operations`);
      
      // Batch operations
      const batches = this._batchOperations(queue);
      let allSuccess = true;
      const syncedOperationIds = [];

      for (const batch of batches) {
        const success = await this._syncBatch(batch);
        if (!success) {
          allSuccess = false;
          this.retryCount++;
          logger.warn(`[SyncService] Sync failed, retry count: ${this.retryCount}`);

          // After max attempts, drop back to the normal sync interval but
          // KEEP the failed operations queued — discarding them silently
          // loses the user's changes (the old "6 trays became 1" class).
          // A non-empty queue also makes the checkout reconcile fail open
          // to the local cart, so the user's intent survives either way.
          if (this.retryCount >= this.config.maxRetries) {
            logger.error('[SyncService] Max retries reached; keeping operations queued for the next interval');
            this.retryCount = 0;
          }

          break; // Stop on first failure
        }
        syncedOperationIds.push(...batch.map(op => op.id));
      }

      // Remove only the operations that actually reached the server —
      // removing the whole queue after a partial failure either lost the
      // failed ops or re-sent (double-applied) the successful ones.
      if (syncedOperationIds.length > 0) {
        cartManager.removeFromSyncQueue(syncedOperationIds);
        cartManager.updateLastSyncTime();
      }

      if (allSuccess && queue.length > 0) {
        this.retryCount = 0; // Reset retry count on success
        this.pendingChanges = false;
        logger.log('[SyncService] Sync completed successfully');
      }

      if (allSuccess && this.wsRefreshPending) {
        this.wsRefreshPending = false;
        this._applyServerRefresh();
      }

      return allSuccess;
    } catch (error) {
      logger.error('[SyncService] Sync failed:', error);
      this.retryCount++;
      return false;
    } finally {
      this.isSyncing = false;
      if (this.isRunning) {
        this._scheduleNextSync();
      }
    }
  }

  /**
   * Clean up old and excessive operations from queue to prevent memory overflow
   * @param {Array} operations - Operations to clean
   * @returns {Array} Cleaned operations array
   * @private
   */
  _cleanupQueue(operations) {
    if (!operations || operations.length === 0) {
      return [];
    }

    const now = Date.now();
    const maxAge = this.config.maxOperationAge;
    const maxSize = this.config.maxQueueSize;

    // Remove old operations
    let cleaned = operations.filter(op => {
      const age = now - (op.queued_at || op.timestamp || 0);
      return age <= maxAge;
    });

    // If still too many operations, keep only the most recent ones
    if (cleaned.length > maxSize) {
      logger.warn(`[SyncService] Queue size ${cleaned.length} exceeds limit ${maxSize}, trimming...`);
      cleaned = cleaned
        .sort((a, b) => (b.queued_at || b.timestamp || 0) - (a.queued_at || a.timestamp || 0))
        .slice(0, maxSize);
    }

    // Log cleanup stats
    if (operations.length !== cleaned.length) {
      const removed = operations.length - cleaned.length;
      logger.log(`[SyncService] Cleaned up ${removed} old/excessive operations from queue`);
    }

    return cleaned;
  }

  /**
   * Batch operations for efficient syncing
   * @param {Array} operations - Operations to batch
   * @returns {Array} Array of batches
   * @private
   */
  _batchOperations(operations) {
    const batches = [];
    
    for (let i = 0; i < operations.length; i += this.config.batchSize) {
      batches.push(operations.slice(i, i + this.config.batchSize));
    }
    
    return batches;
  }

  /**
   * Sync a batch of operations
   * @param {Array} batch - Operations to sync
   * @returns {Promise<boolean>} Success status
   * @private
   */
  async _syncBatch(batch) {
    try {
      // Send operations in queue (timestamp) order so the server replays
      // exactly what the user did. Grouping by type reordered sequences
      // like "remove item, then re-add it" into add-then-remove, deleting
      // the re-added item.
      const validOps = batch.filter(op =>
        op.type === 'clear' ||
        ((op.type === 'add' || op.type === 'set') && op.product_id) ||
        ((op.type === 'update' || op.type === 'remove') && op.item_id)
      );

      // Kept in the same order as validOps for positional result mapping
      const addOperations = validOps.filter(op => op.type === 'add');
      const setOperations = validOps.filter(op => op.type === 'set');

      const serverOperations = validOps.map(op => {
        switch (op.type) {
          case 'clear':
            return { type: 'clear' };
          case 'add':
          case 'set':
            return {
              type: op.type,
              product_id: op.product_id,
              unit_id: op.unit_id,
              quantity: op.quantity,
              price: op.price
              // No item_id - server resolves by product/unit
            };
          case 'update':
            return { type: 'update', item_id: op.item_id, quantity: op.quantity };
          case 'remove':
            return { type: 'remove', item_id: op.item_id };
          default:
            return null;
        }
      }).filter(Boolean);

      if (serverOperations.length === 0) {
        return true; // Nothing to sync
      }

      // Get current cart timestamp
      const cart = cartManager.getCart();
      const timestamp = cart.updated_at_iso;

      // Call batch API
      const response = await this._callBatchAPI(serverOperations, timestamp);
      
      if (response.success) {
        // IMPORTANT: Do NOT replace local cart with server cart data during sync
        // Local cart is authoritative during the session
        // Only update server-assigned IDs for new items
        if (response.results && response.results.length > 0) {
          this._handleBatchResults(response.results, addOperations, setOperations);
        }

        if (response.cart && response.cart.id) {
          cartManager.adoptServerCartId(response.cart.id);
        }

        if (response.latestTimestamp) {
          this.lastServerTimestamp = response.latestTimestamp;
        }

        // NOTE: We intentionally DON'T update discount data from batch sync response
        // Discount updates should come from the dedicated calculateDiscounts() API call
        // to avoid race conditions that cause discount flicker

        // Handle any conflicts passively
        if (response.conflicts && response.conflicts.length > 0) {
          this._handleConflicts(response.conflicts);
        }

        return true;
      } else {
        logger.warn('[SyncService] Batch sync failed:', response.error);
        return false;
      }
    } catch (error) {
      logger.error('[SyncService] Error syncing batch:', error);
      return false;
    }
  }

  /**
   * Call batch API endpoint
   * @param {Array} operations - Operations to send
   * @param {string} timestamp - Current cart timestamp
   * @returns {Promise<Object>} API response
   * @private
   */
  async _callBatchAPI(operations, timestamp) {
    try {
      // Import API client dynamically to avoid circular dependencies
      const { default: apiClient } = await import('./api');
      
      const payload = {
        operations: operations,
        client_timestamp: timestamp
      };

      const deviceId = cartManager.getDeviceId();
      if (deviceId) {
        payload.device_id = deviceId;
      }

      const response = await apiClient.post(this.endpoints.batch, payload);

      return {
        success: true,
        data: response.data,
        results: response.data.results || [],
        cart: response.data.cart || null,  // Full cart data with discount fields
        conflicts: response.data.conflicts || [],
        latestTimestamp: response.headers['x-cart-timestamp']
      };
    } catch (error) {
      if (error.response?.status === 409) {
        // Conflict - handle passively
        return {
          success: true, // Don't retry conflicts
          conflicts: error.response.data?.conflicts || [],
          latestTimestamp: error.response.data?.latest_timestamp
        };
      }
      
      throw error;
    }
  }

  /**
   * Fetch current cart from server
   * @returns {Promise<Object|null>} Server cart data
   * @private
   */
  async _fetchServerCart() {
    try {
      logger.log('[SyncService] Fetching from endpoint:', this.endpoints.get);

      // Import API client dynamically
      const { default: apiClient } = await import('./api');

      const response = await apiClient.get(this.endpoints.get);
      logger.log('[SyncService] Server response status:', response.status);
      logger.log('[SyncService] Server response data:', response.data);

      return response.data;
    } catch (error) {
      logger.error('[SyncService] Error fetching server cart:', error);
      logger.error('[SyncService] Error details:', error.response?.data || error.message);
      return null;
    }
  }

  /**
   * Handle conflicts with passive strategy
   * @param {Array} conflicts - Array of conflicts
   * @private
   */
  _handleConflicts(conflicts) {
    if (!conflicts || conflicts.length === 0) return;

    logger.log(`[SyncService] Handling ${conflicts.length} conflicts passively`);

    // Passive conflict resolution - just log for now
    // In production, you might want to:
    // 1. Show notifications to user about price changes
    // 2. Update local cart with server prices
    // 3. Log analytics about conflicts

    conflicts.forEach(conflict => {
      logger.log(`[SyncService] Conflict: ${conflict.type} - ${conflict.message}`);

      // Example: Handle price changes
      if (conflict.type === 'price_change') {
        logger.log(`[SyncService] Price changed for ${conflict.product_name}: ${conflict.old_price} → ${conflict.new_price}`);
        // Could show a toast notification here
      }

      // Example: Handle stock issues
      if (conflict.type === 'stock_unavailable') {
        logger.log(`[SyncService] Stock unavailable for ${conflict.product_name}`);
        // Could show a notification and suggest alternatives
      }
    });
  }

  /**
   * Get sync status
   * @returns {Object} Current sync status
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      isSyncing: this.isSyncing,
      isOnline: this.isOnline,
      retryCount: this.retryCount,
      queueLength: cartManager.getSyncQueue().length,
      lastSyncTime: cartManager.getConfig().lastSyncTime
    };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - Configuration updates
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    
    // Restart if sync interval changed
    if (newConfig.syncInterval && this.isRunning) {
      this.stop();
      this.start();
    }

    logger.log('[SyncService] Configuration updated:', newConfig);
  }

  /**
   * Handle results from batch API call
   * Maps temporary IDs to server IDs for add operations
   * @param {Array} results - Results from server
   * @param {Array} addOperations - Original add operations with temp IDs
   * @private
   */
  _handleBatchResults(results, addOperations, setOperations = []) {
    try {
      logger.log(`[SyncService] Processing ${results.length} batch results for ${addOperations.length} add + ${setOperations.length} set operations`);

      // Process results in the same order they were sent
      let addOperationIndex = 0;
      let setOperationIndex = 0;

      results.forEach((result) => {
        // Both add and set can create items whose temp ids need mapping
        let originalOp = null;
        if (result.operation === 'add' && addOperationIndex < addOperations.length) {
          originalOp = addOperations[addOperationIndex];
          addOperationIndex++;
        } else if (result.operation === 'set' && setOperationIndex < setOperations.length) {
          originalOp = setOperations[setOperationIndex];
          setOperationIndex++;
        }
        if (!originalOp) {
          // Note: update and remove operations don't need ID mapping since they use server IDs
          return;
        }

        // Find the temporary item in cart by matching product/unit
        const cart = cartManager.getCart();
        const tempItem = cart.items.find(item =>
          item.is_pending &&
          item.product?.id === originalOp.product_id &&
          item.unit?.id === originalOp.unit_id
        );

        if (tempItem) {
          if (result.success && result.item_id) {
            // Success - map to server ID
            logger.log(`[SyncService] Mapping temp item ${tempItem.id} to server ID ${result.item_id}`);
            cartManager.handleServerAddResponse(tempItem.id, {
              id: result.item_id,
              line_total: result.line_total || tempItem.line_total,
              price_at_add: result.price_at_add || tempItem.price_at_add
            });
          } else {
            // Failed - mark as failed
            logger.error(`[SyncService] ${result.operation} operation failed: ${result.error || 'Unknown error'}`);
            cartManager.handleServerAddFailure(tempItem.id, result.error || 'Server error');
          }
        } else {
          logger.warn(`[SyncService] Could not find temp item for ${result.operation} operation`);
        }
      });

    } catch (error) {
      logger.error('[SyncService] Error handling batch results:', error);
    }
  }

  /**
   * Update discount data on local cart items from server response
   * Server is authoritative for discount data - it has full cart context
   * @param {Array} serverItems - Items from server with discount data
   * @private
   */
  _updateDiscountData(serverItems) {
    try {
      const cart = cartManager.getCart();
      let updated = false;

      logger.log(`[SyncService] _updateDiscountData: ${serverItems.length} server items, ${cart.items.length} local items`);

      // Update each local item with matching server item data
      cart.items.forEach((localItem, index) => {
        const localProductId = localItem.product?.id;
        const localUnitId = localItem.unit?.id;

        // Find matching server item
        const serverItem = serverItems.find(si => {
          const serverProductId = si.product?.id || si.product_id;
          const serverUnitId = si.unit?.id || si.unit_id;
          return serverProductId === localProductId && serverUnitId === localUnitId;
        });

        if (serverItem) {
          logger.log(`[SyncService] Matched item: ${localItem.product?.name}, server_qty: ${serverItem.quantity}, discount_percent: ${serverItem.discount_percent}, discounted_price: ${serverItem.discounted_price}, line_total: ${serverItem.line_total}`);

          // Update server_id and is_pending status
          const updatedItem = {
            ...localItem,
            server_id: localItem.server_id || serverItem.id,
            is_pending: false,
          };

          // Server is authoritative for discount data - it has full cart context
          // and knows if PRODUCT_COMBO/all_or_nothing conditions are met
          const serverHasDiscount = serverItem.discount_percent && parseFloat(serverItem.discount_percent) > 0;

          if (serverHasDiscount) {
            // Server has discount - use server discount data
            updatedItem.discount_applied = serverItem.discount_applied || null;
            updatedItem.discount_percent = serverItem.discount_percent;
            updatedItem.discounted_price = serverItem.discounted_price;
            updatedItem.discount_reason = serverItem.discount_reason || '';
            updatedItem.discount_name = serverItem.discount_name || '';
            updatedItem.line_total_before_discount = serverItem.line_total_before_discount;
            updatedItem.line_discount_amount = serverItem.line_discount_amount;
            updatedItem.line_total = serverItem.line_total || localItem.line_total;
          } else {
            // Server says no discount - clear discount fields
            // This handles when conditions are no longer met (e.g., item deleted)
            updatedItem.discount_applied = null;
            updatedItem.discount_percent = 0;
            updatedItem.discounted_price = null;
            updatedItem.discount_reason = '';
            updatedItem.discount_name = '';
            updatedItem.line_total_before_discount = null;
            updatedItem.line_discount_amount = 0;
            // Recalculate line_total without discount
            const quantity = parseFloat(localItem.quantity || 0);
            const priceAtAdd = parseFloat(localItem.price_at_add || 0);
            updatedItem.line_total = quantity * priceAtAdd;
          }

          cart.items[index] = updatedItem;
          updated = true;
        } else {
          logger.warn(`[SyncService] No server match for local item: product=${localProductId}, unit=${localUnitId}`);
        }
      });

      if (updated) {
        // Recalculate cart totals with updated line_totals
        cartManager._updateCartMetadata(cart);
        // Save cart with updated discount data
        localStorage.setItem(cartManager.CART_KEY, JSON.stringify(cart));
        cartManager._notifyListeners();
        logger.log('[SyncService] Updated discount data on local cart items, new total:', cart.total_price);
      }
    } catch (error) {
      logger.error('[SyncService] Error updating discount data:', error);
    }
  }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;